import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { TableCard } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { LoadingDialog } from "ui-components/Alert";
import { getReconsolidateCycleCountReportPreview } from "services/data.inventory.service";

const REASON_MAX_LENGTH = 200;

const PALLET_MODES = [
  { value: "existing", label: "Existing pallet" },
  { value: "new", label: "New pallet" },
];

const TYPE_LABELS = { MATCH: "Matched", EXTRA: "Extra only" };

function ReasonCell({ row, canEdit, onSave }) {
  const savedRef = useRef(row.original.reason ?? "");
  const [text, setText] = useState(savedRef.current);

  useEffect(() => {
    savedRef.current = row.original.reason ?? "";
    setText(savedRef.current);
  }, [row.original]);

  const commit = async () => {
    const trimmed = text.trim();
    if (trimmed === savedRef.current) {
      setText(savedRef.current);
      return;
    }
    try {
      await onSave(row.original, trimmed);
      savedRef.current = trimmed;
      setText(trimmed);
    } catch (e) {
      // the page-level handler reports the error and reloads the table
    }
  };

  return (
    <TextField
      variant="standard"
      value={text}
      disabled={!canEdit}
      placeholder={canEdit ? "Type a reason" : ""}
      inputProps={{ maxLength: REASON_MAX_LENGTH }}
      sx={{ minWidth: "240px" }}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
    />
  );
}

const sameLocation = (a, b) => a.zoneID === b.zoneID && a.locationID === b.locationID;

function defaultInput(line, locations) {
  const exists = locations.some(
    (l) => l.zoneID === line.defaultZone && l.locationID === line.defaultLocation
  );
  return {
    zoneID: exists ? line.defaultZone : null,
    locationID: exists ? line.defaultLocation : null,
    quantity: line.maxQuantity,
    palletMode: line.defaultPalletMode,
  };
}

function ReconsolidateList({
  rows,
  customerCode,
  canReconsolidate,
  canEditReason,
  onSaveReason,
  isSubmitting,
  onRemove,
  onConfirm,
}) {
  const [inputs, setInputs] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  const stkIds = useMemo(() => rows.flatMap((r) => r.stkIds || []), [rows]);

  const { data: preview, isFetching, error } = useQuery({
    queryKey: ["cycle-count-reconsolidate-preview", customerCode, stkIds],
    queryFn: () => getReconsolidateCycleCountReportPreview({ customerCode, stkIds }),
    enabled: stkIds.length > 0,
    cacheTime: 0,
    staleTime: 0,
    select: (res) => res.data.data,
  });

  const lines = useMemo(() => preview?.lines ?? [], [preview]);
  const locations = useMemo(() => preview?.locations ?? [], [preview]);
  const zones = useMemo(() => [...new Set(locations.map((l) => l.zoneID))], [locations]);

  useEffect(() => {
    if (!preview) return;
    setInputs((prev) => {
      const next = {};
      preview.lines.forEach((line) => {
        next[line.key] = prev[line.key] ?? defaultInput(line, preview.locations);
      });
      return next;
    });
  }, [preview]);

  // One report row becomes one or two list lines (matched and/or extra only), or a "cannot
  // reconsolidate" placeholder when there is nothing that can be put back.
  const tableRows = useMemo(
    () =>
      rows.flatMap((row) => {
        const rowLines = lines.filter((l) => l.stkIds.includes(row.stkIds[0]));
        if (rowLines.length === 0) {
          const skipped = Boolean(preview?.skippedStkIds?.includes(row.stkIds[0]));
          return [{ ...row, rowId: row.id, id: `${row.id}:none`, line: null, skipped, type: "" }];
        }
        return rowLines.map((line) => ({
          ...row,
          rowId: row.id,
          id: line.key,
          line,
          skipped: false,
          type: TYPE_LABELS[line.type] ?? line.type,
          missingFrom: line.missingFromLocation ?? "",
          foundAt: line.foundAtLocation ?? "",
        }));
      }),
    [rows, lines, preview]
  );

  const setInput = (key, patch) =>
    setInputs((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const itemError = (item) => {
    if (item.skipped) return "No matching EXTRA - cannot put back. Remove it, or use Close";
    if (!item.line) return "Loading...";
    const input = inputs[item.line.key];
    if (!input) return "Loading...";
    if (!input.zoneID || !input.locationID) return "Choose a zone and location";
    const qty = Number(input.quantity);
    if (!qty || qty <= 0 || qty > item.line.maxQuantity) {
      return `Quantity must be above 0 and up to ${item.line.maxQuantity}`;
    }
    return null;
  };

  const canSubmit =
    canReconsolidate &&
    tableRows.length > 0 &&
    !isSubmitting &&
    !isFetching &&
    tableRows.every((item) => !itemError(item));

  const submit = () => {
    setConfirmOpen(false);
    onConfirm(
      lines.map((line) => ({
        key: line.key,
        quantity: Number(inputs[line.key].quantity),
        zoneID: inputs[line.key].zoneID,
        locationID: inputs[line.key].locationID,
        palletMode: inputs[line.key].palletMode,
      }))
    );
  };

  const locationOptions = (line, zoneID) =>
    locations
      .filter((l) => l.zoneID === zoneID)
      .map((l) => {
        const existing = line.existingLocations.find((e) => sameLocation(e, l));
        return {
          locationID: l.locationID,
          existingQty: existing ? existing.quantity : null,
          group: existing ? "Already holds this SKU" : "Other locations",
        };
      })
      .sort((a, b) => (a.group === b.group ? 0 : a.group === "Already holds this SKU" ? -1 : 1));

  const stockNow = (item) => {
    const input = inputs[item.line.key];
    if (!input?.zoneID || !input?.locationID) return "";
    if (input.palletMode === "new") return "New pallet will be created";
    const existing = item.line.existingLocations.find((e) => sameLocation(e, input));
    return existing
      ? `${existing.quantity} here - added to the existing pallet`
      : "Not here - new pallet will be created";
  };

  const columns = useMemo(
    () => [
      { header: "Cycle Count No", accessorKey: "cycleCountNo" },
      { header: "Type", accessorKey: "type", enableColumnFilter: false },
      { header: "SKU", accessorKey: "skuCode" },
      {
        header: "Expiry Date",
        accessorKey: "expiryDate",
        cell: ({ row }) =>
          row.original.expiryDate ? new Date(row.original.expiryDate).toLocaleDateString("en-GB") : "",
      },
      { header: "MLN", accessorKey: "mln" },
      { header: "Missing From", accessorKey: "missingFrom" },
      { header: "Found At", accessorKey: "foundAt" },
      {
        header: "Qty To Put Back",
        id: "qty",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { line } = row.original;
          const input = line && inputs[line.key];
          if (!input) return "";
          return (
            <TextField
              variant="standard"
              type="number"
              sx={{ width: 90 }}
              value={input.quantity ?? ""}
              helperText={`max ${line.maxQuantity}`}
              inputProps={{ min: 0, max: line.maxQuantity }}
              onChange={(e) => setInput(line.key, { quantity: e.target.value })}
            />
          );
        },
      },
      {
        header: "Zone",
        id: "zone",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { line } = row.original;
          const input = line && inputs[line.key];
          if (!input) return "";
          return (
            <Autocomplete
              disableClearable
              sx={{ width: 170 }}
              options={zones}
              value={input.zoneID ?? null}
              onChange={(e, value) => setInput(line.key, { zoneID: value, locationID: null })}
              renderInput={(params) => <TextField {...params} variant="standard" />}
            />
          );
        },
      },
      {
        header: "Location",
        id: "location",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { line } = row.original;
          const input = line && inputs[line.key];
          if (!input) return "";
          return (
            <Autocomplete
              disableClearable
              disabled={!input.zoneID}
              sx={{ width: 200 }}
              options={input.zoneID ? locationOptions(line, input.zoneID) : []}
              groupBy={(option) => option.group}
              getOptionLabel={(option) =>
                option.existingQty !== null && option.existingQty !== undefined
                  ? `${option.locationID} (${option.existingQty})`
                  : option.locationID
              }
              isOptionEqualToValue={(option, value) => option.locationID === value.locationID}
              value={input.locationID ? { locationID: input.locationID } : null}
              onChange={(e, value) => setInput(line.key, { locationID: value.locationID })}
              renderInput={(params) => <TextField {...params} variant="standard" />}
            />
          );
        },
      },
      {
        header: "Pallet",
        id: "pallet",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { line } = row.original;
          const input = line && inputs[line.key];
          if (!input) return "";
          return (
            <Autocomplete
              disableClearable
              sx={{ width: 160 }}
              options={PALLET_MODES}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={PALLET_MODES.find((m) => m.value === input.palletMode) ?? PALLET_MODES[0]}
              onChange={(e, value) => setInput(line.key, { palletMode: value.value })}
              renderInput={(params) => <TextField {...params} variant="standard" />}
            />
          );
        },
      },
      {
        header: "Stock There Now",
        id: "stockNow",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const item = row.original;
          const err = itemError(item);
          return (
            <MDTypography variant="caption" color={err && err !== "Loading..." ? "error" : "text"}>
              {err ?? stockNow(item)}
            </MDTypography>
          );
        },
      },
      {
        header: "Reason",
        id: "reason",
        accessorKey: "reason",
        cell: ({ row }) => <ReasonCell row={row} canEdit={canEditReason} onSave={onSaveReason} />,
      },
      {
        header: "",
        id: "remove",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <MDButton
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => onRemove(row.original.rowId)}
          >
            remove
          </MDButton>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, inputs, locations, zones, preview, onRemove, canEditReason, onSaveReason]
  );

  return (
    <TableCard sx={{ my: 3 }}>
      {isFetching && <LoadingDialog />}
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        p={2}
      >
        <MDBox>
          <MDTypography variant="h5" color="info">
            Reconsolidation List
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Matched: MIA that has a matching EXTRA (misplaced stock), put back where it went missing.
            Extra only: leftover EXTRA, put into the system as new stock where it was found. Choose
            the location, then click reconsolidate.
          </MDTypography>
        </MDBox>
        <MDButton
          variant="gradient"
          color="warning"
          disabled={!canSubmit}
          onClick={() => setConfirmOpen(true)}
        >
          reconsolidate
        </MDButton>
      </MDBox>
      {error && (
        <MDTypography variant="body2" color="error" sx={{ px: 2, pb: 1 }}>
          {error?.response?.data?.message ?? "Unable to load reconsolidate details."}
        </MDTypography>
      )}
      <BasicTable data={tableRows} columns={columns} />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reconsolidate</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">
            The stock in this list will be put into the chosen locations (added to the existing
            pallet or onto a new pallet, as selected) and the matching MIA and EXTRA records will
            be closed. MIA with no matching EXTRA stays open. This cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setConfirmOpen(false)}>
            cancel
          </MDButton>
          <MDButton variant="gradient" color="warning" onClick={submit} disabled={isSubmitting}>
            confirm reconsolidate
          </MDButton>
        </DialogActions>
      </Dialog>
    </TableCard>
  );
}

export default ReconsolidateList;
